import { useEffect, useMemo, useState } from 'react';

type PageSizeOption = string | number;

type UseLocalPaginationOptions = {
    pageSizeOptions?: PageSizeOption[];
    initialPageSize?: number;
};

export const useLocalPagination = (
    totalItems: number,
    options?: UseLocalPaginationOptions
) => {
    const defaultPageSizeOptions = ['10', '25', '50', '100'];
    const getInitialPageSize = () => {
        const fallback = Number(defaultPageSizeOptions[0]);
        if (options?.initialPageSize && !Number.isNaN(options.initialPageSize)) {
            return options.initialPageSize;
        }

        const firstOption =
            options?.pageSizeOptions?.[0] ?? defaultPageSizeOptions[0];

        const numericValue = Number(firstOption);

        return Number.isNaN(numericValue) ? fallback : numericValue;
    };

    const [pageSize, setPageSize] = useState<number>(getInitialPageSize());
    const [currentPage, setCurrentPage] = useState(1);

    const pageSizeOptions = useMemo(() => {
        const rawOptions = (
            options?.pageSizeOptions ?? defaultPageSizeOptions
        ).map(option => option.toString());

        const uniqueOptions = rawOptions.reduce<string[]>(
            (acc, current) => {
                if (!acc.includes(current)) acc.push(current);
                return acc;
            },
            []
        );

        const orderedOptions = defaultPageSizeOptions.filter(option =>
            uniqueOptions.includes(option)
        );

        uniqueOptions.forEach(option => {
            if (!orderedOptions.includes(option)) {
                orderedOptions.push(option);
            }
        });

        if (!orderedOptions.includes(pageSize.toString())) {
            orderedOptions.push(pageSize.toString());
        }

        return orderedOptions;
    }, [options?.pageSizeOptions, pageSize]);

    const totalPages = useMemo(() => {
        if (!pageSize || Number.isNaN(pageSize)) return 1;

        return Math.max(1, Math.ceil(totalItems / pageSize));
    }, [pageSize, totalItems]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const onChangePage = (page: number) => {
        const nextPage = Math.min(Math.max(1, page), totalPages);
        setCurrentPage(nextPage);
    };

    const onChangePageSize = (size: number) => {
        if (Number.isNaN(size)) return;

        setPageSize(size);
        setCurrentPage(1);
    };

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return {
        currentPage,
        pageSize,
        pageSizeOptions,
        onChangePage,
        onChangePageSize,
        startIndex,
        endIndex,
    };
};
